// E08-t4 (the wiring half) — WHAT THE ADMIN PAGE CAN ACTUALLY BE FED.
//
// Contract: vault/Apps/Forge/Pages/Admin.md f2 ("the guard matrix per KB — role
//   by command, protected branches") and f3 ("quotas and per-file storage usage,
//   live from the accounting ledger").
// Contract: git-host/src/quota.ts::usageSurface — GET /api/:org/:kb/usage,
//   answered verbatim into AdminPageProps.usage.
// Contract: git-host/src/index.ts commandsApi — GET /api/:org/:kb/commands, the
//   KB's command VOCABULARY, derived from the named format's declared `forms:`
//   (Command Layer f1 · 0013: "every declared form becomes a command; nothing is
//   hand-registered"). It is a PROJECTION of format.yaml, so it is asked for
//   rather than enumerated here.
// Contract: web/src/pages/Admin.tsx — "NO FETCHING… the page takes DATA IN PROPS
//   and the container decides where it came from. Inventing a parallel API here
//   would be a lie that compiles." This module is that container's transport, and
//   it holds the same line one level up.
//
// ---------------------------------------------------------------------------
// THE TWO GAPS, STATED RATHER THAN FAKED (they are why this file is short)
// ---------------------------------------------------------------------------
//   1. THERE IS NO ORG-LEVEL MEMBERS ROUTE. git-host serves /api/session (the
//      caller's own memberships) and per-KB routes; nothing answers "who is in
//      this org". So this module offers no `listMembers`, and AdminRoute.tsx
//      shows the reader their OWN membership and says in the page that it is the
//      only one the control plane will tell it about. A fabricated roster would
//      make the governance surface look finished and would be wrong about the one
//      thing an admin opens it for.
//   2. THERE IS NO MATRIX ROUTE, AND NO WRITE FOR ONE. git-host/src/kb.ts builds
//      a KB's matrix as `DEFAULT_GUARD_MATRIX.commands` plus that KB's own
//      protected branches, and serves it to nobody; `guard_rules` rows exist in
//      platform/src/guards.ts::loadGuardMatrix but no HTTP surface reads or
//      writes them. So the container passes the PLATFORM DEFAULT — the same
//      object the git host evaluates with today — and says so on the page.
//
// Shaped like src/api/dashboards.ts, including the late-bound default fetch (see
// src/api/auth.ts for why the binding is a thunk).
import { appFetch } from './transport';
import type { AdminCommand, UsageSurface } from '../pages/Admin';
import type { CommandKind } from '../../../platform/src/guards';

/** git-host/src/quota.ts — the accounting ledger's own surface. */
export const usageEndpoint = (org: string, kb: string): string =>
  `/api/${encodeURIComponent(org)}/${encodeURIComponent(kb)}/usage`;

/** git-host/src/index.ts — the format-derived vocabulary. */
export const commandsEndpoint = (org: string, kb: string): string =>
  `/api/${encodeURIComponent(org)}/${encodeURIComponent(kb)}/commands`;

/** The two questions the Admin container asks. Injected, so no page holds a fetch. */
export interface AdminClient {
  loadUsage(org: string, kb: string): Promise<UsageSurface>;
  loadCommands(org: string, kb: string): Promise<AdminCommand[]>;
}

const KINDS: readonly CommandKind[] = ['read', 'capture', 'command', 'merge', 'govern'];

const num = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/**
 * A refusal is an ordinary answer with the guard NAMED (platform/src/guards.ts),
 * so it travels up in those words rather than flattened into "failed" — the same
 * relay src/api/dashboards.ts performs, because an admin who is refused needs to
 * read WHICH guard refused them.
 */
async function refusalOf(response: Response, path: string): Promise<Error> {
  const body = (await response.json().catch(() => ({}))) as { guard?: string; reason?: string };
  return new Error(
    `${body.guard ? `${body.guard}: ` : ''}${body.reason ?? `GET ${path} answered ${response.status}`}`,
  );
}

export function createAdminClient(
  fetchImpl: typeof fetch = (input, init) => appFetch(input, init),
): AdminClient {
  return {
    async loadUsage(org: string, kb: string): Promise<UsageSurface> {
      const path = usageEndpoint(org, kb);
      const response = await fetchImpl(path, { credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, path);
      const body = (await response.json()) as Record<string, any>;
      // The shape is quota.ts's; the ids are read from the ANSWER and not from the
      // route, because the accounting identity is the control plane's to state.
      return {
        orgId: typeof body.orgId === 'string' ? body.orgId : org,
        kbId: typeof body.kbId === 'string' ? body.kbId : kb,
        usage: { bytes: num(body.usage?.bytes), files: num(body.usage?.files) },
        limits: { kbBytes: num(body.limits?.kbBytes), kbFiles: num(body.limits?.kbFiles) },
        files: Array.isArray(body.files)
          ? body.files
              .filter((file: unknown): file is { path: string; bytes: number } =>
                Boolean(file && typeof (file as { path?: unknown }).path === 'string'),
              )
              .map((file) => ({ path: file.path, bytes: num(file.bytes) }))
          : [],
      };
    },

    async loadCommands(org: string, kb: string): Promise<AdminCommand[]> {
      const path = commandsEndpoint(org, kb);
      const response = await fetchImpl(path, { credentials: 'same-origin' });
      // A KB that declares no format answers 404 with an `error` sentence, and a
      // definition that will not compile answers 422 with another. Neither is an
      // empty vocabulary — they are things the page must be able to SAY — so they
      // travel as refusals rather than as a silent empty matrix.
      if (!response.ok) throw await refusalOf(response, path);
      const body = (await response.json()) as { commands?: Array<Record<string, unknown>> };
      const commands: AdminCommand[] = [];
      const seen = new Set<string>();
      for (const entry of body.commands ?? []) {
        const name = typeof entry?.name === 'string' ? entry.name : null;
        if (name === null || seen.has(name)) continue; // ids are block-scoped; NAMES can repeat
        seen.add(name);
        const kind = entry.kind as CommandKind;
        commands.push({ name, kind: KINDS.includes(kind) ? kind : 'command' });
      }
      return commands;
    },
  };
}
